import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, TrendingDown, Minus, Award, Target,
  Activity, DollarSign, Heart, Users, Clock, ChevronUp, ChevronDown,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BenchmarkAnalytics = () => {
  const [benchmarks, setBenchmarks] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dataSource, setDataSource] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');

  useEffect(() => {
    fetchBenchmarks();
  }, []);

  const fetchBenchmarks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/phase49/benchmarks`);
      const data = await response.json();
      setBenchmarks(data.benchmarks || {});
      setSummary(data.summary || {});
      setDataSource(data.data_source || '');
      setLastUpdated(data.last_updated || '');
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
      toast.error('Failed to load benchmark data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'quality_measures': return Activity;
      case 'operational_metrics': return Target;
      case 'patient_experience': return Heart;
      case 'financial_benchmarks': return DollarSign;
      default: return BarChart3;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'quality_measures': return 'Quality Measures';
      case 'operational_metrics': return 'Operational Metrics';
      case 'patient_experience': return 'Patient Experience';
      case 'financial_benchmarks': return 'Financial';
      default: return category;
    }
  };

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'top_quartile': return 'text-green-600 bg-green-50';
      case 'above_average': return 'text-blue-600 bg-blue-50';
      case 'below_average': return 'text-amber-600 bg-amber-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getPerformanceLabel = (performance) => {
    switch (performance) {
      case 'top_quartile': return 'Top 25%';
      case 'above_average': return 'Above Avg';
      case 'below_average': return 'Below Avg';
      default: return performance;
    }
  };

  const getPerformanceIcon = (performance) => {
    switch (performance) {
      case 'top_quartile': return <ChevronUp className="h-4 w-4 text-green-600" />;
      case 'above_average': return <TrendingUp className="h-4 w-4 text-blue-600" />;
      case 'below_average': return <TrendingDown className="h-4 w-4 text-amber-600" />;
      default: return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  const formatMetricName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const filteredCategories = selectedCategory === 'all' 
    ? Object.keys(benchmarks) 
    : [selectedCategory];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading benchmark data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BarChart3 className="h-10 w-10 text-teal-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800" data-testid="benchmark-title">Benchmark Analytics</h1>
                <p className="text-slate-500">Compare your performance to national hospice standards</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right text-sm text-slate-500">
                <div>Data Source: {dataSource}</div>
                <div>Updated: {new Date(lastUpdated).toLocaleDateString()}</div>
              </div>
              <Button variant="outline" onClick={fetchBenchmarks}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Overall Percentile</p>
                    <p className="text-3xl font-bold text-teal-600">{summary.overall_percentile}%</p>
                  </div>
                  <Award className="h-10 w-10 text-teal-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">Top Quartile</p>
                    <p className="text-3xl font-bold text-green-600">{summary.top_quartile}</p>
                    <p className="text-xs text-green-600">metrics</p>
                  </div>
                  <ChevronUp className="h-10 w-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">Above Average</p>
                    <p className="text-3xl font-bold text-blue-600">{summary.above_average}</p>
                    <p className="text-xs text-blue-600">metrics</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-700">Below Average</p>
                    <p className="text-3xl font-bold text-amber-600">{summary.below_average}</p>
                    <p className="text-xs text-amber-600">metrics</p>
                  </div>
                  <TrendingDown className="h-10 w-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-slate-600">Category:</span>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-64" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.keys(benchmarks).map(cat => (
                <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Benchmark Categories */}
        <div className="space-y-8">
          {filteredCategories.map(category => {
            if (!benchmarks[category]) return null;
            const IconComponent = getCategoryIcon(category);
            const metrics = benchmarks[category];
            
            return (
              <Card key={category} data-testid={`benchmark-${category}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <IconComponent className="h-5 w-5 text-teal-600" />
                    {getCategoryLabel(category)}
                  </CardTitle>
                  <CardDescription>
                    {Object.keys(metrics).length} metrics compared to national benchmarks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {Object.entries(metrics).map(([metricName, metric]) => (
                      <div key={metricName} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700">
                                {metric.description || formatMetricName(metricName)}
                              </span>
                              {getPerformanceIcon(metric.performance)}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className={getPerformanceColor(metric.performance)}>
                              {getPerformanceLabel(metric.performance)}
                            </Badge>
                            <span className="text-lg font-bold text-teal-600 min-w-[80px] text-right">
                              {metric.our_score}{metric.unit === '%' ? '%' : ` ${metric.unit}`}
                            </span>
                          </div>
                        </div>
                        
                        {/* Visual comparison bar */}
                        <div className="relative h-8 bg-slate-100 rounded-lg overflow-hidden">
                          {/* National average marker */}
                          <div 
                            className="absolute top-0 bottom-0 w-0.5 bg-slate-400 z-10"
                            style={{ 
                              left: `${Math.min((metric.national_avg / (metric.top_quartile * 1.2)) * 100, 95)}%` 
                            }}
                          >
                            <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs text-slate-500 whitespace-nowrap">
                              Nat'l Avg: {metric.national_avg}{metric.unit === '%' ? '%' : ''}
                            </div>
                          </div>
                          
                          {/* Top quartile marker */}
                          <div 
                            className="absolute top-0 bottom-0 w-0.5 bg-green-400 z-10"
                            style={{ 
                              left: `${Math.min((metric.top_quartile / (metric.top_quartile * 1.2)) * 100, 95)}%` 
                            }}
                          >
                            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-green-600 whitespace-nowrap">
                              Top 25%: {metric.top_quartile}{metric.unit === '%' ? '%' : ''}
                            </div>
                          </div>
                          
                          {/* Our score bar */}
                          <div 
                            className={`h-full transition-all ${
                              metric.performance === 'top_quartile' ? 'bg-green-500' :
                              metric.performance === 'above_average' ? 'bg-blue-500' :
                              'bg-amber-500'
                            }`}
                            style={{ 
                              width: `${Math.min((metric.our_score / (metric.top_quartile * 1.2)) * 100, 100)}%` 
                            }}
                          />
                        </div>
                        
                        <div className="flex justify-between text-xs text-slate-400 mt-6">
                          <span>0</span>
                          <span>Your Score: {metric.our_score}{metric.unit === '%' ? '%' : ` ${metric.unit}`}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Legend */}
        <Card className="mt-8">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Top 25% (Excellent)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Above National Average</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded"></div>
                <span>Below National Average</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-4 bg-slate-400"></div>
                <span>National Average</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-4 bg-green-400"></div>
                <span>Top 25% Threshold</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BenchmarkAnalytics;
